/**
 * Post-login path resolution for /login: React Router `location.state.from`,
 * `?redirect=`, and OAuth fallback in sessionStorage (Google/Microsoft round-trip).
 */

const STORAGE_KEY = "freemium_post_login_redirect";

/** After OAuth, user+token are stored here until terms are accepted (token is not in localStorage yet). */
const PENDING_OAUTH_TERMS_KEY = "freemium_oauth_pending_terms";

/**
 * @param {object} user
 * @param {string} token
 */
export function storePendingOAuthTerms(user, token) {
  try {
    sessionStorage.setItem(
      PENDING_OAUTH_TERMS_KEY,
      JSON.stringify({ user, token })
    );
  } catch {
    /* ignore */
  }
}

/**
 * @returns {{ user: object, token: string } | null}
 */
export function readPendingOAuthTerms() {
  try {
    const s = sessionStorage.getItem(PENDING_OAUTH_TERMS_KEY);
    if (!s) return null;
    const v = JSON.parse(s);
    if (v && v.user && v.token) return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function clearPendingOAuthTerms() {
  try {
    sessionStorage.removeItem(PENDING_OAUTH_TERMS_KEY);
  } catch {
    /* ignore */
  }
}

function isSameOriginPath(path) {
  return (
    typeof path === "string" &&
    path.startsWith("/") &&
    !path.startsWith("//")
  );
}

function readStoredRedirect() {
  try {
    const s = sessionStorage.getItem(STORAGE_KEY);
    if (isSameOriginPath(s)) return s;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * @param {import('react-router-dom').Location} location
 * @returns {string | null}
 */
export function getPostLoginPath(location) {
  const from = location.state?.from;
  if (from?.pathname?.startsWith("/")) {
    return `${from.pathname}${from.search || ""}`;
  }
  const params = new URLSearchParams(location.search);
  const redirect = params.get("redirect");
  if (!isSameOriginPath(redirect)) return null;
  try {
    return decodeURIComponent(redirect);
  } catch {
    return null;
  }
}

export function clearOAuthStoredRedirect() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Call before redirecting to Google/Microsoft so the OAuth callback (?token= only)
 * can still restore the deep link from sessionStorage.
 * @param {import('react-router-dom').Location} location
 */
export function persistRedirectBeforeOAuth(location) {
  const next = getPostLoginPath(location);
  if (!next) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
}

/**
 * URL/state first, then OAuth-persisted path.
 * @param {import('react-router-dom').Location} location
 * @returns {string | null}
 */
export function resolvePostLoginPath(location) {
  return getPostLoginPath(location) || readStoredRedirect();
}
